import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function AccessDeniedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role } = useAuth();
  const requiredRoles = location.state?.required || [];

  const description = (() => {
    if (!user) return 'You need to be logged in to view this page.';
    if (requiredRoles.length === 0) return 'You do not have access to this page.';

    return `This page is only available for: ${requiredRoles.join(', ')} accounts.`;
  })();

  const primaryCta = (() => {
    if (!user) {
      return {
        label: 'Go to login',
        onClick: () => navigate('/login'),
        testId: 'access-denied-login-btn',
      };
    }

    if (role === 'installer') {
      return {
        label: 'Go to installer dashboard',
        onClick: () => navigate('/installer'),
        testId: 'access-denied-installer-btn',
      };
    }

    return {
      label: 'Go to homeowner dashboard',
      onClick: () => navigate('/dashboard'),
      testId: 'access-denied-homeowner-btn',
    };
  })();

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
      data-testid="access-denied-page"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-red-500 mb-1">
            Access denied
          </p>
          <h1 className="text-xl font-bold text-gray-900 mb-1">
            You don&apos;t have permission to view this page
          </h1>
          <p className="text-sm text-gray-600" data-testid="access-denied-description">
            {description}
          </p>
        </div>

        {user && role && (
          <div className="mb-4 text-xs text-gray-500" data-testid="access-denied-role-info">
            You&apos;re currently signed in as a <span className="font-semibold">{role}</span>{' '}
            account.
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50"
            data-testid="access-denied-home-btn"
          >
            Back to home
          </button>
          <button
            type="button"
            onClick={primaryCta.onClick}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            data-testid={primaryCta.testId}
          >
            {primaryCta.label}
          </button>
        </div>
      </div>
    </div>
  );
}
